import React, { useState } from 'react';
import SafeIcon from '../common/SafeIcon';
import { FiTerminal, FiAward, FiBook, FiArrowLeft, FiPlay, FiCheck, FiCpu, FiShield, FiTrendingUp, FiSearch, FiFileText, FiMonitor, FiZap } from 'react-icons/fi';
import { motion } from 'framer-motion';

const AcademyInfoPages = ({ defaultTab = 'simulation', onBack }) => {
  const [activeTab, setActiveTab] = useState(defaultTab);

  const tabs = [
    { id: 'simulation', label: 'Simulation Engine', icon: FiTerminal },
    { id: 'certification', label: 'Certification Queue', icon: FiAward },
    { id: 'kb', label: 'KB Documentation', icon: FiBook },
  ];

  const content = {
    simulation: (
      <div className="space-y-8">
        <div className="border-b border-slate-100 pb-6 flex items-center space-x-4">
          <div className="w-16 h-16 bg-blue-100 text-blue-600 rounded-2xl flex items-center justify-center shrink-0">
            <SafeIcon icon={FiTerminal} className="text-3xl" />
          </div>
          <div>
            <h2 className="text-3xl font-black text-slate-900 mb-1">Simulation Engine</h2>
            <p className="text-slate-500 text-sm font-medium">The core interactive training environment</p>
          </div>
        </div>
        
        {/* Added Promo Video to Academy Content */}
        <div className="bg-slate-900 rounded-2xl overflow-hidden shadow-xl mb-8 border border-slate-800">
          <video 
            className="w-full aspect-video"
            controls
            poster="https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&q=80&w=1200"
          >
            <source src="https://websitebrokers.org/videos/promovideonetdeskpro.mp4" type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </div>

        <section className="space-y-4">
          <p className="text-slate-600 leading-relaxed">
            Instead of reading boring manuals, analysts are dropped into a simulated "live" environment where they deal with realistic IT incidents. 
          </p>
        </section>

        <section className="space-y-6">
          <div className="bg-slate-50 p-6 rounded-2xl border border-slate-100">
            <h3 className="text-sm font-black text-slate-800 uppercase tracking-widest mb-4 flex items-center">
              <SafeIcon icon={FiPlay} className="mr-2 text-blue-500" />
              How It Works
            </h3>
            <p className="text-sm text-slate-600 leading-relaxed mb-4">
              The engine procedurally generates mock support tickets ranging from simple password resets to complex cloud infrastructure failures.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="bg-white p-3 rounded-xl border border-slate-200 flex items-center space-x-3 shadow-sm">
                <div className="w-8 h-8 rounded-lg bg-orange-100 text-orange-600 flex items-center justify-center shrink-0"><SafeIcon icon={FiShield} /></div>
                <span className="text-xs font-bold text-slate-700">Phishing Triage</span>
              </div>
              <div className="bg-white p-3 rounded-xl border border-slate-200 flex items-center space-x-3 shadow-sm">
                <div className="w-8 h-8 rounded-lg bg-indigo-100 text-indigo-600 flex items-center justify-center shrink-0"><SafeIcon icon={FiCpu} /></div>
                <span className="text-xs font-bold text-slate-700">Server Outages</span>
              </div>
            </div>
          </div>

          <div className="bg-slate-50 p-6 rounded-2xl border border-slate-100">
            <h3 className="text-sm font-black text-slate-800 uppercase tracking-widest mb-4 flex items-center">
              <SafeIcon icon={FiCheck} className="mr-2 text-emerald-500" />
              The Experience & Outcomes
            </h3>
            <ul className="space-y-4">
              <li className="flex items-start space-x-3">
                <div className="mt-0.5 w-5 h-5 rounded-full bg-slate-200 flex items-center justify-center shrink-0"><span className="text-[10px] font-black text-slate-500">1</span></div>
                <div>
                  <p className="text-sm font-bold text-slate-800 mb-1">Diagnostic Decision Trees</p>
                  <p className="text-xs text-slate-500 leading-relaxed">When an analyst opens a ticket, they are presented with choices. They must choose the correct troubleshooting steps, ask the right questions, and follow protocols.</p>
                </div>
              </li>
              <li className="flex items-start space-x-3">
                <div className="mt-0.5 w-5 h-5 rounded-full bg-blue-200 flex items-center justify-center shrink-0"><span className="text-[10px] font-black text-blue-700">2</span></div>
                <div>
                  <p className="text-sm font-bold text-slate-800 mb-1">XP & Grading System</p>
                  <p className="text-xs text-slate-500 leading-relaxed">Every action is graded. Correct actions earn XP (Experience Points) and progress the resolution, while incorrect actions (like clicking a phishing link) result in deductions or simulation failure.</p>
                </div>
              </li>
            </ul>
          </div>
        </section>
      </div>
    ),
    certification: (
      <div className="space-y-8">
        <div className="border-b border-slate-100 pb-6 flex items-center space-x-4">
          <div className="w-16 h-16 bg-emerald-100 text-emerald-600 rounded-2xl flex items-center justify-center shrink-0">
            <SafeIcon icon={FiAward} className="text-3xl" />
          </div>
          <div>
            <h2 className="text-3xl font-black text-slate-900 mb-1">Certification Queue</h2>
            <p className="text-slate-500 text-sm font-medium">Career progression & credentialing system</p>
          </div>
        </div>
        <section className="space-y-4">
          <p className="text-slate-600 leading-relaxed">
            The platform tracks an analyst's performance over time to validate their skills and issue official enterprise credentials.
          </p>
        </section>
        <section className="space-y-4">
          <div className="border border-slate-200 rounded-2xl overflow-hidden">
            <div className="bg-slate-50 p-4 border-b border-slate-200 flex items-center space-x-3">
              <SafeIcon icon={FiTrendingUp} className="text-slate-500" />
              <h3 className="font-black text-slate-800 uppercase tracking-widest text-xs">Progression Track</h3>
            </div>
            <div className="p-6 space-y-6">
              <div className="relative pl-8 border-l-2 border-slate-200 space-y-8">
                <div className="relative">
                  <div className="absolute -left-[41px] top-0 w-5 h-5 rounded-full bg-blue-500 ring-4 ring-white" />
                  <h4 className="text-sm font-bold text-slate-900 mb-1">1. Tier 1 Operations</h4>
                  <p className="text-xs text-slate-500 leading-relaxed">Analysts start by handling Tier 1 tickets. The system tracks their resolution rate, accuracy, and SLA (Service Level Agreement) compliance.</p>
                </div>
                <div className="relative">
                  <div className="absolute -left-[41px] top-0 w-5 h-5 rounded-full bg-slate-200 ring-4 ring-white flex items-center justify-center">
                    <SafeIcon icon={FiCheck} className="text-[10px] text-slate-400" />
                  </div>
                  <h4 className="text-sm font-bold text-slate-900 mb-1">2. Unlocking Levels</h4>
                  <p className="text-xs text-slate-500 leading-relaxed">Once an analyst successfully resolves a specific quota of tickets (e.g., 50 Tier 1 tickets), they unlock access to the Tier 2 queue, which contains much harder, system-level challenges.</p>
                </div>
                <div className="relative">
                  <div className="absolute -left-[41px] top-0 w-5 h-5 rounded-full bg-amber-400 ring-4 ring-white flex items-center justify-center">
                    <SafeIcon icon={FiAward} className="text-[10px] text-white" />
                  </div>
                  <h4 className="text-sm font-bold text-slate-900 mb-1">3. Official Validation</h4>
                  <p className="text-xs text-slate-500 leading-relaxed">The "Queue" is where analysts view milestones. Once hit, they can claim, generate, and print an official "NetDesk Academy" certificate to prove their competency to management.</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    ),
    kb: (
      <div className="space-y-8">
        <div className="border-b border-slate-100 pb-6 flex items-center space-x-4">
          <div className="w-16 h-16 bg-purple-100 text-purple-600 rounded-2xl flex items-center justify-center shrink-0">
            <SafeIcon icon={FiBook} className="text-3xl" />
          </div>
          <div>
            <h2 className="text-3xl font-black text-slate-900 mb-1">KB Documentation</h2>
            <p className="text-slate-500 text-sm font-medium">The "open book" reference library</p>
          </div>
        </div>
        <section className="space-y-4">
          <p className="text-slate-600 leading-relaxed">
            By forcing analysts to consult the KB during simulations, the platform trains them to rely on official company documentation rather than guessing, which is a critical skill in enterprise IT support.
          </p>
        </section>
        <section className="grid grid-cols-1 gap-4">
          <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm flex items-start space-x-4">
            <div className="w-10 h-10 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
              <SafeIcon icon={FiFileText} />
            </div>
            <div>
              <h3 className="text-sm font-bold text-slate-900 mb-2">Standard Operating Procedures (SOPs)</h3>
              <p className="text-xs text-slate-500 leading-relaxed">Contains all the official enterprise rules. For example, if an analyst gets a ticket about a "CEO" asking for gift cards, they can search the KB for the "Executive Impersonation" protocol to know exactly how to handle it.</p>
            </div>
          </div>
          <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm flex items-start space-x-4">
            <div className="w-10 h-10 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center shrink-0">
              <SafeIcon icon={FiSearch} />
            </div>
            <div>
              <h3 className="text-sm font-bold text-slate-900 mb-2">Technical Guides</h3>
              <p className="text-xs text-slate-500 leading-relaxed">Includes deep-dive diagnostic guides for specific errors (like Windows BSOD Error 41, VPN latency troubleshooting, or database locks).</p>
            </div>
          </div>
        </section>
        <div className="p-6 bg-slate-900 rounded-2xl text-white flex items-center justify-between">
          <div>
            <p className="text-xs font-black uppercase tracking-widest text-blue-400 mb-1">Core Philosophy</p>
            <p className="text-sm font-medium">"Search first, escalate second."</p>
          </div>
          <SafeIcon icon={FiZap} className="text-3xl text-slate-700" />
        </div>
      </div>
    )
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col md:flex-row">
      {/* Sidebar Navigation */}
      <div className="w-full md:w-80 bg-white border-r border-slate-200 p-8 flex flex-col">
        <button 
          onClick={onBack}
          className="flex items-center space-x-2 text-slate-400 hover:text-slate-900 transition-colors mb-12 group"
        >
          <SafeIcon icon={FiArrowLeft} className="group-hover:-translate-x-1 transition-transform" />
          <span className="text-xs font-black uppercase tracking-widest">Back to Home</span>
        </button>

        <div className="mb-10">
          <div className="flex items-center space-x-2 mb-2">
            <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center text-white shadow-lg shadow-blue-200">
              <SafeIcon icon={FiMonitor} />
            </div>
            <span className="font-black tracking-tighter text-slate-900">ACADEMY INFO</span>
          </div>
          <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">Platform Capabilities</p>
        </div>

        <div className="space-y-2 flex-1">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`w-full flex items-center space-x-3 px-4 py-3 rounded-xl text-sm font-bold transition-all ${
                activeTab === tab.id 
                  ? 'bg-slate-900 text-white shadow-lg shadow-slate-200' 
                  : 'text-slate-500 hover:bg-slate-50'
              }`}
            >
              <SafeIcon icon={tab.icon} className={activeTab === tab.id ? 'text-blue-400' : ''} />
              <span>{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Content Area */}
      <div className="flex-1 p-8 md:p-20 overflow-y-auto">
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-3xl mx-auto bg-white p-10 md:p-14 rounded-[2.5rem] border border-slate-200 shadow-sm"
        >
          {content[activeTab]}
        </motion.div>
      </div>
    </div>
  );
};

export default AcademyInfoPages;